/**
 * Verdicts Service
 * 
 * Firestore CRUD operations for verdict records.
 * Schema: users/{uid}/verdicts/{vid}
 * 
 * AI analysis now handled via triggerAnalysisAtSave → verdict.forensicRoast
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Verdict, VerdictInputs, VerdictCalculations, VerdictFlags, AIInsight } from '@/types';
// narration.ts types retained for legacy Firestore reads only

/**
 * Defensive read for reconciliation status.
 * Legacy verdicts may not have this field.
 */
export function getReconciliationStatus(verdict: { sold?: boolean; reconciliationStatus?: string | null }): string {
  if (verdict.reconciliationStatus) return verdict.reconciliationStatus;
  if (verdict.sold) return 'reported';
  return 'none';
}

/**
 * Get reference to user's verdicts collection
 */
function getVerdictsCollection(userId: string) {
  return collection(db, 'users', userId, 'verdicts');
}

/**
 * Get reference to a specific verdict document
 */
function getVerdictDoc(userId: string, verdictId: string) {
  return doc(db, 'users', userId, 'verdicts', verdictId);
}

/**
 * Create a new verdict
 * 
 * @param userId - User's Firebase UID
 * @param inputs - Raw inputs from calculator form
 * @param calculations - Computed values from pricing engine
 * @param flags - Validation flags and exception markers
 */
export async function createVerdict(
  userId: string,
  inputs: VerdictInputs,
  calculations: VerdictCalculations,
  flags: VerdictFlags,
): Promise<Verdict> {
  const now = new Date().toISOString();

  const verdictData: Omit<Verdict, 'id'> = {
    userId,
    inputs,
    calculations,
    flags,
    sold: false,
    soldPrice: null,
    soldDate: null,
    currency: inputs.currency,
    createdAt: now,
    updatedAt: now,
  };

  const docRef = await addDoc(getVerdictsCollection(userId), verdictData);
  
  return {
    id: docRef.id,
    ...verdictData,
  };
}

/**
 * Get a single verdict by ID
 */
export async function getVerdict(userId: string, verdictId: string): Promise<Verdict | null> {
  const docSnap = await getDoc(getVerdictDoc(userId, verdictId));
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Verdict;
}

/**
 * Get all verdicts for a user (most recent first)
 */
export async function getVerdicts(userId: string, maxResults = 50): Promise<Verdict[]> {
  const q = query(
    getVerdictsCollection(userId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  // Use getDocs (cache + server merge) so locally-written verdicts appear
  // immediately. Previously used getDocsFromServer to bypass stale cache
  // after deletions, but that broke saves — local addDoc writes aren't on
  // the server yet when portfolio loads. Soft-delete staleness is handled
  // by the client-side `!v.deletedAt` filter in useVerdicts.
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Verdict[];
}

/**
 * Update verdict (e.g., mark as sold)
 */
export async function updateVerdict(
  userId: string,
  verdictId: string,
  updates: Partial<Pick<Verdict, 'sold' | 'soldPrice' | 'soldDate' | 'soldNote' | 'aiInsight' | 'aiAnalysis' | 'forensicRoast' | 'reconciliationStatus' | 'linkedTransactionId' | 'workflowStage'>>
): Promise<void> {
  const docRef = getVerdictDoc(userId, verdictId);
  
  await updateDoc(docRef, {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Update verdict inputs and recalculated values (Adjust Inputs flow).
 * Overwrites inputs, calculations, and flags — preserves everything else
 * (sold status, AI insight, quotes, timestamps).
 */
export async function updateVerdictInputs(
  userId: string,
  verdictId: string,
  inputs: VerdictInputs,
  calculations: VerdictCalculations,
  flags: VerdictFlags
): Promise<void> {
  const docRef = getVerdictDoc(userId, verdictId);
  await updateDoc(docRef, {
    inputs,
    calculations,
    flags,
    currency: inputs.currency,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Clear tpLinkedArtworkId on any transactions pointing to this verdict.
 * Prevents orphaned pointers when a verdict is deleted or unsold.
 */
async function clearLinkedTransactions(userId: string, verdictId: string): Promise<void> {
  const txnRef = collection(db, 'users', userId, 'transactions');
  const q = query(txnRef, where('tpLinkedArtworkId', '==', verdictId));
  const snapshot = await getDocs(q);
  await Promise.all(
    snapshot.docs.map((txnDoc) =>
      updateDoc(doc(db, 'users', userId, 'transactions', txnDoc.id), {
        tpLinkedArtworkId: null,
      })
    )
  );
}

/**
 * Soft-delete a verdict (sets deletedAt timestamp, 30-day grace period)
 */
export async function deleteVerdict(userId: string, verdictId: string): Promise<void> {
  const docRef = getVerdictDoc(userId, verdictId);
  await updateDoc(docRef, {
    deletedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  // Clear linked transaction pointers to avoid orphans
  await clearLinkedTransactions(userId, verdictId);
}

/**
 * Permanently delete a verdict (for cleanup after grace period)
 */
export async function permanentlyDeleteVerdict(userId: string, verdictId: string): Promise<void> {
  await deleteDoc(getVerdictDoc(userId, verdictId));
}

/**
 * Mark verdict as sold
 */
export async function markAsSold(
  userId: string,
  verdictId: string,
  soldPrice: number,
  soldNote?: string,
  soldDate?: string,
  soldBuyer?: string,
  reconciliationStatus?: 'reported' | 'confirmed' | 'confirmed_manual',
  linkedTransactionId?: string | null,
): Promise<void> {
  await updateVerdict(userId, verdictId, {
    sold: true,
    soldPrice,
    soldDate: soldDate || new Date().toISOString(),
    reconciliationStatus: reconciliationStatus || 'reported',
    linkedTransactionId: linkedTransactionId || null,
    workflowStage: 'sold',
    ...(soldNote !== undefined ? { soldNote } : {}),
    ...(soldBuyer !== undefined ? { soldBuyer } : {}),
  });
}

/**
 * Reset a verdict's sold status
 */
export async function unmarkSold(
  userId: string,
  verdictId: string
): Promise<void> {
  const docRef = getVerdictDoc(userId, verdictId);
  await updateDoc(docRef, {
    sold: false,
    soldPrice: null,
    soldDate: null,
    soldNote: deleteField(),
    reconciliationStatus: 'none',
    linkedTransactionId: null,
    workflowStage: 'audit',
    updatedAt: new Date().toISOString(),
  });
  // Clear linked transaction pointers to avoid orphans
  await clearLinkedTransactions(userId, verdictId);
}

/**
 * Get verdict count for a user
 */
export async function getVerdictCount(userId: string): Promise<number> {
  const snapshot = await getDocs(getVerdictsCollection(userId));
  return snapshot.size;
}

/**
 * Backfill AI insight for a legacy verdict
 * Use this when reopening a verdict that was saved before aiInsight was added
 */
export async function backfillAIInsight(
  userId: string,
  verdictId: string,
  aiInsight: AIInsight
): Promise<void> {
  await updateVerdict(userId, verdictId, { aiInsight });
}

/**
 * Atomically link a verdict to a bank transaction.
 * Sets reconciliationStatus to 'confirmed' on the verdict
 * and tpLinkedArtworkId on the transaction.
 */
export async function linkVerdictToTransaction(
  userId: string,
  verdictId: string,
  transactionId: string
): Promise<void> {
  const batch = writeBatch(db);

  const verdictRef = doc(db, `users/${userId}/verdicts/${verdictId}`);
  const txnRef = doc(db, `users/${userId}/transactions/${transactionId}`);

  batch.update(verdictRef, {
    linkedTransactionId: transactionId,
    reconciliationStatus: 'confirmed',
  });

  batch.update(txnRef, {
    tpLinkedArtworkId: verdictId,
  });

  await batch.commit();
}
